import { useEffect, useState } from "react";
import { OWNERS, createEvent, updateEvent, type EventInput } from "../actions";
import { useMyProfile, useTagDefs, type CrmEvent } from "../data";
import { IcCheck } from "./icons";

/**
 * Creating and editing an event.
 *
 * Four fields do the work — name, date, time, place — because an event usually
 * gets entered while somebody is already talking about it. Everything else is
 * optional and sits below.
 *
 * The slug deserves its explanation on screen rather than in a tooltip: it is
 * the string the website's registration form posts, and getting it wrong is the
 * difference between sign-ups landing on the event and sitting unattached. It is
 * derived from the name by default and only shown when somebody opens it.
 */

function localInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}`;
}

const WEEKDAY_NAMES = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];
/** Single Hebrew letters for the chips. Saturday is ״ש״ and Friday ״ו״, the way a
 *  Hebrew calendar prints them. */
const WEEKDAY_LETTERS = ["א", "ב", "ג", "ד", "ה", "ו", "ש"];

/**
 * Moves a datetime-local string forward to the nearest of the given weekdays.
 *
 * This is the behaviour the product owner asked for in as many words: "חייב
 * שיהיה אפשר לבחור לפי יום בשבוע. כי תאריך זה לא משתנה כשאני משנה את זה." Picking
 * "every Sunday" has to move the date to a Sunday — otherwise the series says
 * Sunday while its first meeting is on a Monday, and the form is arguing with
 * itself.
 *
 * The time of day is kept. Today counts as a match, so choosing the weekday it
 * already is changes nothing.
 */
function snapToWeekday(value: string, weekdays: number[]): string {
  if (weekdays.length === 0) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  if (weekdays.includes(d.getDay())) return value;

  for (let ahead = 1; ahead <= 7; ahead++) {
    const next = new Date(d);
    next.setDate(next.getDate() + ahead);
    if (weekdays.includes(next.getDay())) return localInput(next);
  }
  return value;
}

/** Tonight is rarely the answer; the next round hour tomorrow usually is. */
function defaultStart(): Date {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(19, 0, 0, 0);
  return d;
}

export function EventSheet({
  event,
  onClose,
  onCreated,
}: {
  event?: CrmEvent;
  onClose: () => void;
  onCreated?: (eventId: string) => void;
}) {
  const editing = Boolean(event);
  const personTags = useTagDefs("person");

  const [title, setTitle] = useState(event?.title ?? "");
  const [slug, setSlug] = useState(event?.slug ?? "");
  const [showSlug, setShowSlug] = useState(false);
  const [startAt, setStartAt] = useState(() =>
    localInput(event?.startAt?.toDate?.() ?? defaultStart()),
  );
  const [allDay, setAllDay] = useState(event?.allDay === true);
  const [locationName, setLocationName] = useState(event?.location?.name ?? "");
  const [locationAddress, setLocationAddress] = useState(event?.location?.address ?? "");
  const [description, setDescription] = useState(event?.description ?? "");
  /**
   * A new event belongs to whoever is creating it, unless they say otherwise.
   *
   * Left empty, an event has no owner, and everything downstream then has to
   * guess: the Google Calendar mirror falls back to whoever created it, which
   * put a rabbi's classes in his wife's calendar because she happened to be the
   * account signed in. Today's cards have the same problem in reverse — an
   * unowned event is shown to both of them.
   */
  const myProfile = useMyProfile();
  const [owners, setOwners] = useState<string[]>(event?.owners ?? []);
  const [ownersSeeded, setOwnersSeeded] = useState(false);

  useEffect(() => {
    // Only for a new event, only once, and never over a choice already made.
    if (editing || ownersSeeded || owners.length > 0) return;
    const mine = myProfile?.ownerSlug;
    if (!mine) return;
    setOwners([mine]);
    setOwnersSeeded(true);
  }, [editing, ownersSeeded, owners.length, myProfile?.ownerSlug]);
  const [capacity, setCapacity] = useState(
    event?.capacity == null ? "" : String(event.capacity),
  );
  const [registrationOpen, setRegistrationOpen] = useState(event?.registrationOpen !== false);
  const [audience, setAudience] = useState<string[]>(event?.audienceTagIds ?? []);

  const [freq, setFreq] = useState("");
  /** Which days of the week a weekly or biweekly series meets. Empty means the
   *  server falls back to the weekday of the start date. */
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [ends, setEnds] = useState("open");
  const [untilDate, setUntilDate] = useState("");
  const [count, setCount] = useState("");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ linked: number; occurrences: number; text: string | null } | null>(
    null,
  );

  /**
   * The rule in words, so the reader can check it before it writes twelve
   * documents. "כל שבוע ביום שישי" is checkable; "weekly" is not.
   */
  const recurrenceSummary = (() => {
    if (freq === "") return "";
    const d = new Date(startAt);
    if (Number.isNaN(d.getTime())) return "";
    // The chosen days, not the date's day: those are the same thing until
    // somebody picks two days a week, and then only this one is true.
    const chosen = weekdays.length > 0 ? [...weekdays].sort((a, b) => a - b) : [d.getDay()];
    const days = chosen.map((n) => WEEKDAY_NAMES[n]).join(", ");
    if (freq === "weekly") return `כל שבוע ביום ${days}`;
    if (freq === "biweekly") return `כל שבועיים ביום ${days}`;
    if (freq === "monthly") return `כל חודש ב-${d.getDate()} בחודש`;
    return `כל שנה ב-${d.getDate()}/${d.getMonth() + 1}`;
  })();

  /** Weekly and biweekly meet on weekdays; monthly and yearly meet on a date. */
  const picksWeekdays = freq === "weekly" || freq === "biweekly";

  /**
   * Turning a weekday on or off, and moving the date to match.
   *
   * Moving the date is the whole point of the control. The last day cannot be
   * turned off — a weekly series with no day is not a rule, and silently falling
   * back to the date's own weekday would leave the chips showing something
   * other than what was saved.
   */
  function toggleWeekday(day: number) {
    const next = weekdays.includes(day)
      ? weekdays.filter((d) => d !== day)
      : [...weekdays, day].sort((a, b) => a - b);
    if (next.length === 0) return;
    setWeekdays(next);
    setStartAt((current) => snapToWeekday(current, next));
  }

  /**
   * Choosing a frequency seeds the chips from the date already on the form.
   *
   * Without it the chips would open empty, and an empty selection means "use the
   * date's weekday" — true, but it reads as though no day has been chosen.
   */
  function chooseFreq(next: string) {
    setFreq(next);
    if ((next === "weekly" || next === "biweekly") && weekdays.length === 0) {
      const d = new Date(startAt);
      if (!Number.isNaN(d.getTime())) setWeekdays([d.getDay()]);
    }
  }

  /**
   * Editing the date by hand keeps a single-day selection honest.
   *
   * Only when exactly one day is selected: moving a "Sunday and Wednesday"
   * series onto a Tuesday is a start date inside the series, not a change of
   * rule, and rewriting his two choices because he adjusted the time would be
   * the control fighting him.
   */
  function changeStartAt(value: string) {
    setStartAt(value);
    if (weekdays.length !== 1) return;
    const d = new Date(value);
    if (!Number.isNaN(d.getTime()) && d.getDay() !== weekdays[0]) setWeekdays([d.getDay()]);
  }

  /**
   * What is missing, if anything.
   *
   * The save button was live on an empty form: the server refused it, so nothing
   * broke, but the reader learned that pressing the button produces an error
   * rather than that the form is not finished. A required field should look
   * required before it is submitted.
   */
  const missing = [
    title.trim().length < 2 ? "שם האירוע" : null,
    startAt === "" || Number.isNaN(new Date(startAt).getTime()) ? "תאריך" : null,
  ].filter((x): x is string => x !== null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !busy && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, busy]);

  async function save() {
    if (missing.length > 0) {
      setErr(`חסר: ${missing.join(" ו")}. מלאו ונסו שוב.`);
      return;
    }
    setBusy(true);
    setErr(null);

    const payload: EventInput = {
      title: title.trim(),
      startAt: new Date(startAt).toISOString(),
      allDay,
      locationName: locationName.trim(),
      locationAddress: locationAddress.trim(),
      description: description.trim(),
      owners,
      capacity: capacity.trim() === "" ? null : Number(capacity),
      registrationOpen,
      audienceTagIds: audience,
    };
    if (slug.trim() !== "") payload.slug = slug.trim();

    if (!editing && freq !== "") {
      payload.recurrence = {
        freq: freq as "weekly" | "biweekly" | "monthly" | "yearly",
        byWeekday: picksWeekdays ? weekdays : [],
        // Only one of the two ever travels: the server rejects both, because
        // whichever it honoured first would be a guess about what was meant.
        untilDate: ends === "until" && untilDate !== "" ? untilDate : null,
        count: ends === "count" && count !== "" ? Number(count) : null,
      };
    }

    try {
      if (editing && event) {
        const r = await updateEvent({ ...payload, eventId: event.id });
        setDone({ linked: r.registrationsLinked, occurrences: 0, text: null });
        setTimeout(onClose, r.registrationsLinked > 0 ? 1600 : 800);
      } else {
        const r = await createEvent(payload);
        setDone({
          linked: r.registrationsLinked,
          occurrences: r.occurrencesCreated,
          text: r.recurrenceText,
        });
        setTimeout(
          () => {
            onCreated?.(r.eventId);
            onClose();
          },
          r.registrationsLinked > 0 || r.occurrencesCreated > 0 ? 2000 : 900,
        );
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לשמור. נסו שוב.");
      setBusy(false);
    }
  }

  return (
    <>
      <div className="scrim" onClick={busy ? undefined : onClose} />
      <div className="sheet" role="dialog" aria-modal="true" aria-label={editing ? "עריכת אירוע" : "אירוע חדש"}>
        <div className="sheet-grip" />

        {done ? (
          <div className="qa-done">
            <div className="qa-done-mark">
              <IcCheck size={26} />
            </div>
            <h3>{editing ? "שמרנו" : "יצרנו את האירוע"}</h3>
            <p>
              {done.text && `${done.text}. `}
              {done.occurrences > 0 && `יצרנו ${done.occurrences} מפגשים קרובים, ונוסיף עוד מעצמנו. `}
              {done.linked > 0
                ? `חיברנו לאירוע ${done.linked} הרשמות שחיכו.`
                : done.occurrences > 0
                  ? "לכל מפגש יש הרשמות ונוכחות בנפרד."
                  : "אפשר להוסיף נרשמים, או לחכות להרשמות מהאתר."}
            </p>
          </div>
        ) : (
          <>
            <div className="sheet-head">
              <h2>{editing ? "עריכת אירוע" : "אירוע חדש"}</h2>
            </div>

            <div className="form-field">
              <span className="form-label">שם האירוע *</span>
              <input
                className="form-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="שבת קהילתית · הפנינג סוכות · ערב נשים"
                autoFocus
                disabled={busy}
              />
            </div>

            <div className="form-field">
              <span className="form-label">מתי *</span>
              <input
                className="form-input"
                type={allDay ? "date" : "datetime-local"}
                value={allDay ? startAt.slice(0, 10) : startAt}
                onChange={(e) =>
                  changeStartAt(allDay ? `${e.target.value}T09:00` : e.target.value)
                }
                disabled={busy}
              />
              <label
                className="t-md"
                style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}
              >
                <input
                  type="checkbox"
                  checked={allDay}
                  onChange={(e) => setAllDay(e.target.checked)}
                  disabled={busy}
                />
                כל היום, בלי שעה
              </label>
              <span className="form-hint">התאריך העברי מחושב ונשמר באירוע אוטומטית.</span>
            </div>

            <div className="form-field">
              <span className="form-label">איפה</span>
              <input
                className="form-input"
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                placeholder="בית חב״ד · הגן · אולם"
                disabled={busy}
              />
              <input
                className="form-input"
                style={{ marginTop: 8 }}
                value={locationAddress}
                onChange={(e) => setLocationAddress(e.target.value)}
                placeholder="כתובת (לא חובה)"
                disabled={busy}
              />
            </div>

            <div className="form-field">
              <span className="form-label">שיוך</span>
              <div className="chips">
                {OWNERS.map((o) => (
                  <button
                    key={o.slug}
                    type="button"
                    className={owners.includes(o.slug) ? "tagchip tagchip-on" : "tagchip"}
                    aria-pressed={owners.includes(o.slug)}
                    onClick={() =>
                      setOwners((prev) =>
                        prev.includes(o.slug) ? prev.filter((x) => x !== o.slug) : [...prev, o.slug],
                      )
                    }
                    disabled={busy}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-field">
              <span className="form-label">פרטים (לא חובה)</span>
              <textarea
                className="form-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                disabled={busy}
                style={{ resize: "vertical", minHeight: 66 }}
              />
            </div>

            <div className="form-field">
              <span className="form-label">מספר מקומות (לא חובה)</span>
              <input
                className="form-input"
                type="number"
                min={1}
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                placeholder="בלי הגבלה"
                disabled={busy}
              />
              <label
                className="t-md"
                style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}
              >
                <input
                  type="checkbox"
                  checked={registrationOpen}
                  onChange={(e) => setRegistrationOpen(e.target.checked)}
                  disabled={busy}
                />
                ההרשמה פתוחה
              </label>
            </div>

            {personTags.data.length > 0 && (
              <div className="form-field">
                <span className="form-label">קהל יעד (לא חובה)</span>
                <div className="chips">
                  {personTags.data
                    .filter((t) => t.status !== "hidden")
                    .slice(0, 24)
                    .map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        className={audience.includes(t.id) ? "tagchip tagchip-on" : "tagchip"}
                        onClick={() =>
                          setAudience((prev) =>
                            prev.includes(t.id) ? prev.filter((x) => x !== t.id) : [...prev, t.id],
                          )
                        }
                        disabled={busy}
                      >
                        {t.name}
                      </button>
                    ))}
                </div>
                <span className="form-hint">
                  למי האירוע מיועד. זה עוזר בהזמנות, ולא מגביל מי יכול להירשם.
                </span>
              </div>
            )}

            {/*
              Recurrence, on create only.

              Editing it is deliberately not offered: the date is per-occurrence,
              so pushing a new start down a series would collapse every week onto
              one day. Changing when a series happens means a new series, and
              saying so is better than a control that half-works.
            */}
            {!editing && (
              <div className="form-field">
                <span className="form-label">חוזר</span>
                <div className="chips">
                  {[
                    { id: "", label: "פעם אחת" },
                    { id: "weekly", label: "כל שבוע" },
                    { id: "biweekly", label: "כל שבועיים" },
                    { id: "monthly", label: "כל חודש" },
                    { id: "yearly", label: "כל שנה" },
                  ].map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      className={freq === f.id ? "tagchip tagchip-on" : "tagchip"}
                      onClick={() => chooseFreq(f.id)}
                      disabled={busy}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                {picksWeekdays && (
                  <div className="chips" style={{ marginTop: 10 }}>
                    {WEEKDAY_LETTERS.map((letter, day) => (
                      <button
                        key={day}
                        type="button"
                        className={weekdays.includes(day) ? "tagchip tagchip-on" : "tagchip"}
                        onClick={() => toggleWeekday(day)}
                        disabled={busy}
                        aria-pressed={weekdays.includes(day)}
                        aria-label={WEEKDAY_NAMES[day]}
                        title={WEEKDAY_NAMES[day]}
                        style={{ minWidth: 38, justifyContent: "center" }}
                      >
                        {letter}
                      </button>
                    ))}
                  </div>
                )}

                {freq !== "" && (
                  <>
                    <span className="form-hint">
                      {recurrenceSummary} · כל מפגש יקבל הרשמות ונוכחות בנפרד.
                    </span>

                    {/*
                      Why this sentence exists.

                      The product owner read the list of occurrences and asked
                      "למה אירוע הוא רק ל־12 פעמים ולא לתמיד?" — reasonably, since
                      twelve is what he could see. A series without an end really
                      does run forever; twelve weeks is only how far ahead the
                      meetings are written, and a nightly job keeps pushing the
                      edge out. That is an implementation detail he should never
                      have had to infer from a list length.
                    */}
                    {ends === "open" && (
                      <span className="form-hint">
                        בלי תאריך סיום, האירוע חוזר לתמיד. ברשימה יופיעו המפגשים של שלושת
                        החודשים הקרובים, ואנחנו מוסיפים עוד כל יום. לא צריך לחדש כלום.
                      </span>
                    )}

                    <div className="chips" style={{ marginTop: 10 }}>
                      {[
                        { id: "open", label: "בלי תאריך סיום" },
                        { id: "until", label: "עד תאריך" },
                        { id: "count", label: "מספר פעמים" },
                      ].map((e) => (
                        <button
                          key={e.id}
                          type="button"
                          className={ends === e.id ? "tagchip tagchip-on" : "tagchip"}
                          onClick={() => setEnds(e.id)}
                          disabled={busy}
                        >
                          {e.label}
                        </button>
                      ))}
                    </div>

                    {ends === "until" && (
                      <input
                        className="form-input"
                        style={{ marginTop: 8 }}
                        type="date"
                        value={untilDate}
                        min={startAt.slice(0, 10)}
                        onChange={(e) => setUntilDate(e.target.value)}
                        disabled={busy}
                      />
                    )}
                    {ends === "count" && (
                      <input
                        className="form-input"
                        style={{ marginTop: 8 }}
                        type="number"
                        min={2}
                        max={200}
                        value={count}
                        onChange={(e) => setCount(e.target.value)}
                        placeholder="מספר מפגשים"
                        disabled={busy}
                      />
                    )}
                  </>
                )}
              </div>
            )}

            {/* The website's join key. Hidden until asked for, because most of
                the time the derived value is right and explaining it up front
                would put a technical field above the name of the event. */}
            <div className="form-field">
              {showSlug ? (
                <>
                  <span className="form-label">מזהה לטפסים באתר</span>
                  <input
                    className="form-input"
                    dir="ltr"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    placeholder="chanukah-2026"
                    disabled={busy}
                  />
                  <span className="form-hint">
                    זה המזהה שטופס ההרשמה באתר שולח. אם הוא תואם, ההרשמות מתחברות לאירוע
                    לבד. אם תשאירו ריק, ניצור אותו מהשם.
                  </span>
                </>
              ) : (
                <button type="button" className="faint" onClick={() => setShowSlug(true)}>
                  הגדרת מזהה לטפסים באתר
                </button>
              )}
            </div>

            {err && <div className="form-error">{err}</div>}

            <div className="btn-row">
              <button
                className="btn btn-primary"
                onClick={save}
                disabled={busy || missing.length > 0}
                title={missing.length > 0 ? `חסר: ${missing.join(" ו")}` : undefined}
              >
                {busy ? "שומרים…" : editing ? "שמרו" : "צרו אירוע"}
              </button>
              <button className="btn" onClick={onClose} disabled={busy}>
                ביטול
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
